import { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useClerkClientAuth } from '@client/hooks/useClerkClientAuth';
import { toast } from 'sonner';
import {
  fetchClientByClientIdRequest,
  fetchClientsForAdvisorByAdvisorIdRequest,
  selectMaybeClient,
  selectSessionAdvisor,
} from '../model';
import { builtEnvironment } from '@shared/builtEnvironment';
import { isRunningLocal } from '@shared/isRunningEnvironment';
import { isNullOrUndefined } from '@shared/isNullOrUndefined';

interface UseNavigateToIntakeFormResult {
  navigateToIntakeForm: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

interface UseNavigateToIntakeFormOptions {
  clientId?: string;
}

const LOCAL_SYNC_RETRY_DELAYS_MS = [0, 500, 1_000, 2_000, 3_000] as const;

function wait(milliseconds: number): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, milliseconds));
}

export function useNavigateToIntakeForm(
  options?: UseNavigateToIntakeFormOptions,
): UseNavigateToIntakeFormResult {
  const { getClerkToken } = useClerkClientAuth();
  const { clientId: routeClientId } = useParams<{ clientId?: string }>();
  const client = useSelector(selectMaybeClient);
  const advisor = useSelector(selectSessionAdvisor);
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const navigateToIntakeForm = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getClerkToken();
      if (!token) {
        setError('Authentication required. Please sign in again.');
        toast.error('Authentication required', { description: 'Please sign in again.' });
        setIsLoading(false);
        return;
      }
      const currentClientId = options?.clientId ?? routeClientId ?? client?.clientId;

      if (!currentClientId) {
        const errorMessage = 'Client ID not found. Please try again.';
        setError(errorMessage);
        toast.error('Failed to load intake form', {
          description: errorMessage,
        });
        return;
      }

      const apiBase = `/${isRunningLocal() ? 'dev' : builtEnvironment}/api`;

      if (client?.formReferenceId) {
        await fetch(`${apiBase}/reopen-form/${currentClientId}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      }

      const response = await fetch(`${apiBase}/client/${currentClientId}/form-reference`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage =
          errorData?.errorMessage ?? 'Failed to load intake form. Please try again.';
        setError(errorMessage);
        toast.error('Failed to load intake form', {
          description: errorMessage,
        });
        return;
      }

      const { formReferenceId } = await response.json();
      const baseUrl = window.location.origin;
      const formUrl = `${baseUrl}/onboarding-form/${formReferenceId}`;
      window.open(formUrl, '_blank', 'noopener,noreferrer');

      const refreshClientData = () => {
        dispatch(fetchClientByClientIdRequest(currentClientId));
        if (!isNullOrUndefined(advisor?.advisorId)) {
          dispatch(
            fetchClientsForAdvisorByAdvisorIdRequest({
              advisorId: advisor!.advisorId,
              isInvalidation: true,
            }),
          );
        }
      };

      const reconcileLocalProviderResponse = async (): Promise<boolean> => {
        for (const delay of LOCAL_SYNC_RETRY_DELAYS_MS) {
          if (delay > 0) await wait(delay);

          const syncResponse = await fetch(`${apiBase}/local/form-provider-response-sync`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ clientId: currentClientId }),
          });

          if (!syncResponse.ok) {
            throw new Error('Failed to synchronize the completed intake form.');
          }

          const { state } = await syncResponse.json();
          if (state !== 'pending') return true;
        }

        return false;
      };

      const handleFocus = async () => {
        window.removeEventListener('focus', handleFocus);

        try {
          const isSynchronized = isRunningLocal() ? await reconcileLocalProviderResponse() : true;
          refreshClientData();

          // The advisor can briefly return before submitting. In that case, listen for the next
          // return instead of treating an unfinished provider response as a successful refresh.
          if (!isSynchronized) {
            window.addEventListener('focus', handleFocus);
          }
        } catch {
          // Keep the existing database state usable and let another focus return retry the local
          // reconciliation. The provider response remains authoritative and no success is shown.
          refreshClientData();
          if (isRunningLocal()) {
            window.addEventListener('focus', handleFocus);
          }
        }
      };
      window.addEventListener('focus', handleFocus);
    } catch (err) {
      const errorMessage = 'An error occurred while loading the intake form. Please try again.';
      setError(errorMessage);
      toast.error('Failed to load intake form', {
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    getClerkToken,
    options?.clientId,
    routeClientId,
    client?.clientId,
    client?.formReferenceId,
    advisor,
    dispatch,
  ]);

  return {
    navigateToIntakeForm,
    isLoading,
    error,
  };
}
